import React, { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, OutlinedQuestionCircleIcon, SearchIcon, SyncAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { type AdminSplaCandidateFilters, useAdminSplaCandidates } from '../../../hooks/useAdminSplaBindings';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import { SplaSubjectCell } from '../../utils/splaSubjects';
import type { SplaCandidate } from '../../../types/models/Spla';
import type { ApiResponseSplaCandidateDismissed } from '../../../types/api/ApiResponseSpla';
import SplaBindingModal, { type SplaBindingPrefill } from './SplaBindingModal';

interface DismissModalProps {
  candidate: SplaCandidate;
  onClose: () => void;
  /** Performs the dismiss POST; resolves `true` on success (the parent owns the toasts). */
  onDismiss: (candidate: SplaCandidate) => Promise<boolean>;
}

function SplaCandidateDismissModal({ candidate, onClose, onDismiss }: DismissModalProps) {
  const [isDismissing, setIsDismissing] = useState(false);

  const handleConfirm = async () => {
    setIsDismissing(true);
    const ok = await onDismiss(candidate);
    setIsDismissing(false);
    if (ok) {
      onClose();
    }
  };

  const subjectName = candidate.subject.label ?? candidate.subject.ref;

  return (
    <Modal variant="small" isOpen onClose={onClose} aria-label="Dismiss candidate">
      <ModalHeader title="Dismiss candidate" titleIconVariant="warning" />
      <ModalBody>
        <Content component="p">
          Dismiss the discovery of <strong>{candidate.software}</strong> on <strong>{subjectName}</strong>?
        </Content>
        <Content component="p">
          This is <strong>permanent</strong>: the agent&apos;s re-reports refresh a discovery&apos;s
          snapshots but never resurrect a dismissal.
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="danger"
          onClick={() => void handleConfirm()}
          isLoading={isDismissing}
          isDisabled={isDismissing}
        >
          Dismiss
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isDismissing}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface SplaCandidatesTabProps {
  /** Defers every admin fetch until the page's staff check has passed. */
  isStaff: boolean;
  /** Bumped by the Bindings tab when a binding create/delete changed binding_count. */
  refreshKey: number;
  /** Tell the Bindings tab a binding was created from the queue. */
  onBindingsChanged: () => void;
  /** Tell the Consumption tab a new binding computed a consumption row. */
  onConsumptionChanged: () => void;
}

/**
 * The agent discovery queue — what the manager-agent saw running ("VM broker runs windows_server"),
 * awaiting a staff attribution decision. ADVISORY ONLY: the agent never writes consumption or
 * bindings, and the embedded client suggestion is resolved from the NetBox owner — a hint that is
 * often wrong for VMs on shared hypervisors. Staff make the call by creating a binding.
 */
function SplaCandidatesTab({ isStaff, refreshKey, onBindingsChanged, onConsumptionChanged }: SplaCandidatesTabProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [unboundOnly, setUnboundOnly] = useState(false);
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const filters = useMemo<AdminSplaCandidateFilters>(
    () => ({ unboundOnly, includeDismissed }),
    [unboundOnly, includeDismissed],
  );
  const { candidates, loading, error, refetch } = useAdminSplaCandidates(isStaff, filters, refreshKey);

  const [bindCandidate, setBindCandidate] = useState<SplaCandidate | null>(null);
  const [dismissCandidate, setDismissCandidate] = useState<SplaCandidate | null>(null);

  const prefill = useMemo<SplaBindingPrefill | null>(
    () =>
      bindCandidate !== null
        ? {
            subjectType: bindCandidate.subject.type,
            subjectRef: bindCandidate.subject.ref,
            subjectLabel: bindCandidate.subject.label,
            suggestedClientId: bindCandidate.suggestion.client_id,
            suggestedClientName: bindCandidate.suggestion.client_name,
          }
        : null,
    [bindCandidate],
  );

  // The saved binding itself lives on the Bindings tab — here only the queue row changes (its
  // binding_count bumps, or it drops out under "unbound only").
  const handleBindingSaved = useCallback(() => {
    void refetch();
    onBindingsChanged();
    onConsumptionChanged();
  }, [refetch, onBindingsChanged, onConsumptionChanged]);

  const handleDismiss = useCallback(
    async (candidate: SplaCandidate): Promise<boolean> => {
      try {
        const response = await apiFetch(`/admin/spla/bindings/candidates/${candidate.id}/dismiss`, {
          method: 'POST',
        });
        // Branch on the JSend body.status, NEVER response.ok.
        const body = (await response.json().catch(() => null)) as ApiResponseSplaCandidateDismissed | null;
        if (body?.status === 'success' && body.data?.dismissed) {
          addNotification({
            title: 'Candidate dismissed',
            srTitle: 'Success alert',
            description: `${candidate.software} on ${candidate.subject.label ?? candidate.subject.ref} leaves the queue permanently.`,
            variant: 'success',
          });
          setDrawerOpen(true);
          void refetch();
          return true;
        }
        addNotification({
          title: 'Could not dismiss the candidate',
          srTitle: 'Error alert',
          description: body?.message ?? 'Could not dismiss the candidate. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        return false;
      } catch (err) {
        addNotification({
          title: 'Could not dismiss the candidate',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        return false;
      }
    },
    [apiFetch, addNotification, setDrawerOpen, refetch],
  );

  const rowActions = (candidate: SplaCandidate): IAction[] => [
    {
      title: 'Create binding…',
      onClick: () => setBindCandidate(candidate),
    },
    candidate.dismissed_at === null
      ? {
          title: 'Dismiss',
          isDanger: true,
          onClick: () => setDismissCandidate(candidate),
        }
      : {
          title: 'Dismiss',
          isAriaDisabled: true,
          tooltipProps: { content: 'Already dismissed — a dismissal is permanent.' },
        },
  ];

  const renderSuggestionCell = (candidate: SplaCandidate) => {
    const { suggestion } = candidate;
    if (suggestion.client_name !== null) {
      return (
        <>
          Suggested: {suggestion.client_name}{' '}
          <Tooltip content="Resolved from the NetBox owner — often wrong for VMs on shared hypervisors. YOU pick the client.">
            <Icon aria-label="About this suggestion">
              <OutlinedQuestionCircleIcon />
            </Icon>
          </Tooltip>
        </>
      );
    }
    if (suggestion.netbox_owner !== null) {
      return (
        <Content component="small">
          NetBox owner &quot;{suggestion.netbox_owner}&quot; matches no client
        </Content>
      );
    }
    return <Content component="small">No suggestion</Content>;
  };

  const renderContent = () => {
    if (loading && candidates.length === 0) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load the discovery queue"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (candidates.length === 0) {
      return (
        <EmptyState titleText="The queue is empty" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Nothing awaits an attribution decision{unboundOnly || includeDismissed ? ' under the current filters' : ''}.
            The manager-agent&apos;s spla module fills this queue as it discovers Microsoft software running
            on managed hosts.
          </EmptyStateBody>
          {(unboundOnly || includeDismissed) && (
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button
                  variant="primary"
                  onClick={() => {
                    setUnboundOnly(false);
                    setIncludeDismissed(false);
                  }}
                >
                  Clear filters
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          )}
        </EmptyState>
      );
    }

    return (
      <Table aria-label="SPLA discovery candidates" variant="compact">
        <Thead>
          <Tr>
            <Th>Subject</Th>
            <Th>Software</Th>
            <Th>Cores</Th>
            <Th>Reported by</Th>
            <Th>Suggestion</Th>
            <Th>Bindings</Th>
            <Th>Seen</Th>
            {includeDismissed && <Th>Dismissed</Th>}
            <Th screenReaderText="Row actions" />
          </Tr>
        </Thead>
        <Tbody>
          {candidates.map((candidate) => (
            <Tr key={candidate.id}>
              <Td dataLabel="Subject">
                <SplaSubjectCell
                  type={candidate.subject.type}
                  subjectRef={candidate.subject.ref}
                  name={candidate.subject.label ?? candidate.subject.ref}
                />
              </Td>
              <Td dataLabel="Software">
                <Label color="purple" isCompact>
                  {candidate.software}
                </Label>
              </Td>
              <Td dataLabel="Cores">{candidate.core_count ?? ''}</Td>
              <Td dataLabel="Reported by">{candidate.reported_by}</Td>
              <Td dataLabel="Suggestion">{renderSuggestionCell(candidate)}</Td>
              <Td dataLabel="Bindings">
                {candidate.binding_count > 0 ? (
                  <Tooltip content="This subject already has bindings (any license).">
                    <Badge isRead screenReaderText="bindings already on this subject">
                      {candidate.binding_count}
                    </Badge>
                  </Tooltip>
                ) : (
                  ''
                )}
              </Td>
              <Td dataLabel="Seen">
                <Content component="small">
                  {formatWallClockDateTime(candidate.first_seen_at)} →{' '}
                  {formatWallClockDateTime(candidate.last_seen_at)}
                </Content>
              </Td>
              {includeDismissed && (
                <Td dataLabel="Dismissed">
                  {candidate.dismissed_at !== null ? (
                    <Label color="grey" isCompact>
                      {formatWallClockDateTime(candidate.dismissed_at)}
                    </Label>
                  ) : (
                    ''
                  )}
                </Td>
              )}
              <Td isActionCell>
                <ActionsColumn items={rowActions(candidate)} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem alignSelf="center">
            <Checkbox
              id="spla-candidates-unbound"
              label="Hide already-bound subjects"
              isChecked={unboundOnly}
              onChange={(_event, checked) => setUnboundOnly(checked)}
            />
          </ToolbarItem>
          <ToolbarItem alignSelf="center">
            <Checkbox
              id="spla-candidates-dismissed"
              label="Show dismissed"
              isChecked={includeDismissed}
              onChange={(_event, checked) => setIncludeDismissed(checked)}
            />
          </ToolbarItem>
          <ToolbarGroup align={{ default: 'alignEnd' }}>
            <ToolbarItem>
              <Button
                variant="secondary"
                icon={<SyncAltIcon />}
                onClick={() => void refetch()}
                isLoading={loading}
                isDisabled={loading}
              >
                Refresh
              </Button>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
      {renderContent()}
      {bindCandidate && (
        <SplaBindingModal
          key={bindCandidate.id}
          isOpen
          binding={null}
          prefill={prefill}
          onClose={() => setBindCandidate(null)}
          onSaved={handleBindingSaved}
        />
      )}
      {dismissCandidate && (
        <SplaCandidateDismissModal
          candidate={dismissCandidate}
          onClose={() => setDismissCandidate(null)}
          onDismiss={handleDismiss}
        />
      )}
    </>
  );
}

export default SplaCandidatesTab;
